/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description page component
 */
import { useEffect } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { App, BadgeProps, Divider, Flex, Skeleton } from 'antd';
import { PageContainer } from '@ant-design/pro-components';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';

// import SpecialNotification from './SpecialNotification';
import NotificationBar from 'components/subscriptions/settings/NotificationBar';
import AppResult, { RESULT_TYPES } from 'components/widgets/AppResult';
import PageHelp from 'components/help/PageHelp';
import { useAppStoreActions, useAppStoreValues } from 'store/app';
import usePersistenceStore from 'store/persistence';
import { getHomePath } from 'utils/router';
import EventTracker from 'utils/eventTracker';
import { VARIABLES } from 'styles/theme';

/**
 * navigation prompt message
 */
const NAVIGATION_PROMPT_MESSAGE = 'Your changes have not been saved. Are you sure you want to leave this page?';

const LOADING_SKELTON = (
	<Flex align='center'>
		<Skeleton.Input active className='w-75' />
		<Divider type='vertical' />
		<Skeleton.Input active />
	</Flex>
);

const PAGE_BADGE_PROPS: BadgeProps = {
	color: VARIABLES.WHITE_COLOR,
	style: {
		marginLeft: 4,
		borderRadius: 900,
		color: VARIABLES.PRIMARY_COLOR,
		boxShadow: `0 0 0 1px ${VARIABLES.PRIMARY_COLOR_3}`,
	},
	overflowCount: 9999,
	showZero: true,
};

interface ICustomProps {
	error?: boolean;
	notFound?: boolean;
	noData?: boolean;
	promptMessage?: string;
	entity?: {
		title: React.ReactNode;
		isLoading: boolean;
	};
	showSubtitleSeparator?: boolean;
}

/**
 * Page component
 */
// TODO - add notification banner
const Page: React.FC<PageProps & ICustomProps> = (props) => {
	const { modal } = App.useApp();
	const { setSelectedMenu } = useAppStoreActions();
	const { setupInfo } = useAppStoreValues();
	const isBillingCustomer = !!setupInfo?.isBillingCustomer;
	const { allowNavigation, actions } = usePersistenceStore.getState();
	const navigate = useNavigate();
	const location = useLocation();
	const blocker = useBlocker(!allowNavigation);
	const {
		item,
		children,
		title,
		className,
		error,
		notFound,
		noData,
		extra = [],
		onBack,
		loading,
		entity,
		tags,
		subTitle,
		showSubtitleSeparator,
		...restProps
	} = props;
	const { isSettingRoute } = item || {};

	// entity title
	const validEntityTitle = (_.isString(entity?.title) && entity?.title) || '';
	const validTitle = _.first(_.compact([_.isString(title) && title, item?.search?.title, item?.name])) || '';
	const documentTitle = validEntityTitle && validTitle ? `${validEntityTitle} - ${validTitle}` : validTitle;

	// Set document title (waits for entity to finish loading so the title reflects loaded data)
	useEffect(() => {
		if (entity?.isLoading) {
			return;
		}
		document.title = documentTitle;
	}, [documentTitle, entity?.isLoading]);

	// Track pageview on URL change — use page-level title only (no entity)
	useEffect(() => {
		EventTracker.trackPageView(validTitle);
	}, [validTitle, location.pathname]);

	useEffect(() => {
		setSelectedMenu(item || null);
	}, [item]);

	useEffect(() => {
		if (blocker.state === 'blocked') {
			modal.confirm({
				closable: true,
				title: 'Discard Changes?',
				content: props.promptMessage ?? NAVIGATION_PROMPT_MESSAGE,
				okText: 'Yes, discard',
				onOk: () => {
					blocker.proceed();
					actions.setAllowNavigation(true);
				},
				onCancel: () => {
					blocker.reset();
				},
				autoFocusButton: 'cancel',
			});
		}
	}, [blocker]);

	const onBackButtonClick = () => {
		if (_.isFunction(onBack)) {
			onBack();
		}
		// can go back
		else if (history.state?.key) {
			history.back();
		}
		// go home
		else {
			navigate(getHomePath());
		}
	};

	const getContent = () => {
		if (error) {
			return <AppResult {...RESULT_TYPES.serverError} />;
		}

		if (notFound) {
			return <AppResult {...RESULT_TYPES.pageNotFound} />;
		}

		if (noData) {
			return <AppResult {...RESULT_TYPES.noData} />;
		}

		return children;
	};

	// Handle title and subtitle layout internally
	const getSubtitle = () => {
		if (!showSubtitleSeparator) {
			return subTitle;
		}

		return (
			<Flex align='center' gap={8}>
				<Divider type='vertical' className='h-4 border-[#D4E6FF]' />
				{subTitle}
			</Flex>
		);
	};

	return (
		<>
			{/* <SpecialNotification /> */}
			<PageContainer
				fixedHeader
				breadcrumb={{}}
				title={entity?.isLoading ? LOADING_SKELTON : entity?.title || title || item?.name}
				tags={!entity?.isLoading && tags}
				subTitle={getSubtitle()}
				affixProps={{ offsetTop: isSettingRoute ? 0 : 90 }}
				loading={{ size: 'default', spinning: !!loading }}
				className={clsx(className, { 'settings-page-container': isSettingRoute })}
				onBack={item?.isExtraRoute ? onBackButtonClick : undefined}
				extra={_.concat(
					!isBillingCustomer &&
						!_.isEmpty(item?.faqCategories) &&
						((<PageHelp item={item} key='page-help' />) as React.ReactNode),
					!_.isEmpty(extra) && <Divider key='divider' type='vertical' />,
					extra,
				)}
				{...restProps}
			>
				<NotificationBar />
				<Flex vertical gap={16}>
					{getContent()}
				</Flex>
			</PageContainer>
		</>
	);
};

export { NAVIGATION_PROMPT_MESSAGE, PAGE_BADGE_PROPS };
export default Page;
